/**
 * @fileoverview Pure helpers for the "Add link" flow of the student deliverable
 * drawer (AR-678). Local copy — the TaskTracker drawer keeps its own
 * (project pattern: copy per module, never share components across modules).
 */

/** Outcome of an add-link attempt, used to route the UI feedback. */
export type AddLinkOutcome = 'success' | 'invalid' | 'error'

/**
 * Normalizes and validates a user-typed URL (mirrors the prototype's
 * `normalizeLinkUrl`): trims, rejects inner whitespace, prefixes `https://`
 * when the protocol is missing, accepts only http(s) and requires a dotted
 * hostname. Returns the normalized URL (the one POSTed to the backend) or
 * null when invalid.
 */
export function normalizeLinkUrl(rawUrl: string): string | null {
  const trimmed = rawUrl.trim()
  if (!trimmed || /\s/.test(trimmed)) return null
  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
  try {
    const parsed = new URL(candidate)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
    if (!parsed.hostname || !parsed.hostname.includes('.')) return null
    return parsed.toString()
  } catch {
    return null
  }
}

/** Backend deadline format from GET /api/student_tasks/:id (strftime %d/%m/%Y). */
const BR_DATE_PATTERN = /^\d{2}\/\d{2}\/\d{4}$/

/**
 * Parses the deadline into the very end of its day, or null when unparsable.
 * The backend sends `dd/mm/yyyy` — `new Date('15/07/2026')` is Invalid Date
 * and `new Date('05/07/2026')` would be May 7th, so that shape is parsed
 * explicitly; anything else falls back to standard (ISO) parsing.
 */
function parseDeadlineEndOfDay(deadline: string): Date | null {
  if (BR_DATE_PATTERN.test(deadline)) {
    const [day, month, year] = deadline.split('/').map(Number)
    return new Date(year, month - 1, day, 23, 59, 59, 999)
  }
  const date = new Date(deadline)
  if (Number.isNaN(date.getTime())) return null
  const endOfDay = new Date(date)
  endOfDay.setHours(23, 59, 59, 999)
  return endOfDay
}

/**
 * A link is late when it was added after the end of the deadline's day
 * (mirrors the prototype's per-file rule: `new Date(uploadedAt) > dueDate`
 * with the due date at the end of the day). Defensive: unparsable dates are
 * never "late".
 */
export function isLinkLate(createdAt: string, deadline: string | null | undefined): boolean {
  if (!createdAt || !deadline) return false
  const due = parseDeadlineEndOfDay(deadline)
  if (!due) return false
  const created = new Date(createdAt)
  return !Number.isNaN(created.getTime()) && created > due
}

/** Formats the link's createdAt for the "Submitted at" line; falls back to raw. */
export function formatLinkSubmittedAt(createdAt: string, locale: string): string {
  if (!createdAt) return ''
  const date = new Date(createdAt)
  if (Number.isNaN(date.getTime())) return createdAt
  return date.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
